import type { InternalAxiosRequestConfig } from "axios";
import type { InterceptorConfig } from "./types";
import { shouldLog, sanitizeHeaders } from "./utils/logging";
import { HeaderRuleEngine } from "./strategies/header-rule-processor";

/**
 * Header rule engine instance (singleton for performance)
 */
const headerRuleEngine = new HeaderRuleEngine();

export function createRequestInterceptor(config: InterceptorConfig) {
  return async (
    axiosConfig: InternalAxiosRequestConfig,
  ): Promise<InternalAxiosRequestConfig> => {
    const { tokenStorage, customHeaders, headerRules } = config;

    // Apply pattern-based header rules first (they may remove headers)
    // Uses Strategy pattern for extensible rule processing
    if (headerRules && headerRules.length > 0 && axiosConfig.headers) {
      await headerRuleEngine.processRules(headerRules, axiosConfig);
    }

    // Add authorization token (unless removed by header rules)
    if (tokenStorage) {
      try {
        const token = await Promise.resolve(tokenStorage.getAccessToken());
        if (
          token &&
          axiosConfig.headers &&
          !axiosConfig.headers.has("Authorization")
        ) {
          axiosConfig.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        if (shouldLog(config)) {
          console.warn("[API] Failed to get access token:", error);
        }
      }
    }

    // Re-apply header rules so "remove" rules can strip Authorization for unauthenticated endpoints (e.g. generate-otp)
    if (headerRules && headerRules.length > 0 && axiosConfig.headers) {
      await headerRuleEngine.processRules(headerRules, axiosConfig);
    }

    // Apply custom headers (static headers)
    if (customHeaders && axiosConfig.headers) {
      Object.entries(customHeaders).forEach(([key, value]) => {
        if (!axiosConfig.headers?.has(key)) {
          axiosConfig.headers.set(key, value);
        }
      });
    }

    // Set default Content-Type if not present in the headers
    if (axiosConfig.headers && !axiosConfig.headers["Content-Type"]) {
      axiosConfig.headers["Content-Type"] = "application/json";
    }

    // Avoid stale responses from HTTP caches / proxies (skip if caller set explicitly)
    if (axiosConfig.headers) {
      if (!axiosConfig.headers.has("Cache-Control")) {
        axiosConfig.headers.set(
          "Cache-Control",
          "no-cache, no-store, max-age=0",
        );
      }
      if (!axiosConfig.headers.has("Pragma")) {
        axiosConfig.headers.set("Pragma", "no-cache");
      }
    }

    if (shouldLog(config)) {
      const safeHeaders = axiosConfig.headers
        ? sanitizeHeaders(
            axiosConfig.headers as unknown as Record<string, unknown>,
          )
        : {};

      console.log(
        `[API Request] ${axiosConfig.method?.toUpperCase()} ${axiosConfig.baseURL}${axiosConfig.url}`,
        {
          params: axiosConfig.params,
          data: axiosConfig.data,
          headers: safeHeaders,
        },
      );
    }

    return axiosConfig;
  };
}

export function createRequestErrorHandler(config: InterceptorConfig) {
  return (error: Error): Promise<never> => {
    if (shouldLog(config)) {
      console.error("[API Request Error]", error);
    }
    return Promise.reject(error);
  };
}
